import React, { useState } from 'react'
import { CircularProgress, Menu, MenuItem, IconButton, Typography, List, ListItem, Box } from '@mui/material'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import ProviderRecommendationComponent from './provider-recommendation.component'
import ConfirmationDialogComponent from '../common/confirmation-dialog.component'
import { useCurrentUser } from '../../../../utils/use-current-user'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import { environment } from '../../../../environments/environment'
import MedChoicesComponent from '../common/med-choices.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { profiler } from '../../../../utils/profiler';

interface IProps {
  patientId: string
  title: string
  currPrg: any
  totalRecomNeedsUpdate?: any,
  isDisabled?: boolean
  prgStatus: boolean
  currStage: any
  careplanId: string
  isRecomMed?: boolean
  isDiscontinue?: boolean
  onRefClick?: any
  onProviderSubmit: any
  classes: ClassNameMap
}

const LongMenu: React.FC<IProps> = ({ patientId, title, careplanId, totalRecomNeedsUpdate, isDisabled = false, isRecomMed, isDiscontinue, onRefClick, currPrg, prgStatus, currStage, onProviderSubmit, classes }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const [addmedicationCard, setAddmedicationCard] = useState(false)
  const [currentPlanAction, setCurrentPlanAction] = useState(null)
  const [planloading, setPlanLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [isMedChoices, setIsMedChoices] = useState(false)
  const { currentRole } = useCurrentUser()
  const patientService = new PatientService()
  const { updatePatientData, updatePatientDataFromResponse } = useUpdateDataAction(patientId)
  const multiProgramConfirm = useSelector((state: RootState) => state.multiProgramConfirm)
  const prgPendingCount = multiProgramConfirm.find((m) => m.program === currPrg)?.pending || 0

  let options = ['enrollment', 'data_intake'].includes(currStage)
    ? ['Referral']
    : (currStage === 'med_baseline')
      ? isRecomMed
        ? ['Regenerate care plan', 'Provider recommendation', 'Reset care plan', 'Referral', 'Medication choices']
        : (prgPendingCount === 0)
          ? ['Generate care plan', 'Provider recommendation', 'Reset care plan', 'Referral', 'Medication choices']
          : ['Regenerate care plan', 'Provider recommendation', 'Reset care plan', 'Referral', 'Medication choices']
      : ['Move to baseline', 'Referral', 'Medication choices']
  options = [...options, 'Medication exclusion']
  options = options.sort()

  const handleConfirmClose = () => setConfirmOpen(false)
  const handleConfirmResetClose = () => setConfirmResetOpen(false)
  const closeMedChoicesDialog = () => setIsMedChoices(false)

  const confirmStatus = (param: boolean) => {
    if (param) {
      setConfirmOpen(false)
      setPlanLoading(true)
      handleClose()
      regenerateCarePlan()
    }
  }

  const confirmResetStatus = (param: boolean) => {
    if (param) {
      setConfirmResetOpen(false)
      setPlanLoading(true)
      resetCarePlan()
      handleClose()
    }
  }

  const printInstructions = (currInstObj: any) => {
    return Object.keys(currInstObj)?.length > 1 ? <List sx={{ padding: '0px 0px 5px 0.85rem' }}>
      {Object.keys(currInstObj)?.map((d: any) => {
        return <ListItem className={classes.puList} key={`inst-${d}`}>
          <Typography variant='body2' component="span" className={classes.evalBoxVal}>
            {currInstObj[d]}
          </Typography>
        </ListItem>
      })
      }
    </List>
      : currInstObj[0] || ''
  }

  const getInstructions = (info = [] as any) => {
    return <Box sx={{ maxHeight: '40rem', overflow: 'auto', minHeight: '1rem' }}>
      <Typography variant="body2" className={classes.desc}>
        {printInstructions(info?.instructions)}
      </Typography>
    </Box>
  }

  const confirmMedRestriction = () => {
    patientService.getMedRestrictions(patientId, currPrg).then((res: any) => {
      toast.warning(getInstructions(res.data), {
        position: 'bottom-center',
        autoClose: false,
      })
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const regenerateCarePlan = () => {
    profiler.startFlow('Regenerate Care Plan')
    profiler.start('regenerateCarePlan API call')
    patientService.regenerateCarePlan(patientId, careplanId).then((res: any) => {
      profiler.end('regenerateCarePlan API call')
      profiler.start('setPlanLoading(false)')
      setPlanLoading(false)
      profiler.end('setPlanLoading(false)')
      profiler.start('updatePatientData (total)')
      const patient = res?.data?.patient
      if (patient) {
        // Use response directly — regenerateCarePlan already returns full patient details
        updatePatientDataFromResponse(patient, { isMeasurement: true, isTreatments: true, isConditions: true, flowName: 'Regenerate Care Plan' })
      } else {
        // Fallback for legacy response (just true/false)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, flowName: 'Regenerate Care Plan' })
      }
    }).catch((error) => {
      profiler.end('regenerateCarePlan API call')
      setPlanLoading(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const resetCarePlan = () => {
    patientService.resetCarePlan(patientId, careplanId).then((res: any) => {
      setPlanLoading(false)
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    }).catch((error) => {
      setPlanLoading(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const handleClick = (event: any) => setAnchorEl(event.currentTarget)

  const handleClose = () => {
    setAnchorEl(null)
    setCurrentPlanAction(null)
  }
  const handleDialogClose = () => setAddmedicationCard(false)

  const handlePlanAction = (e: any) => {
    let planAction = e.target.innerText
    setCurrentPlanAction(planAction)
    if (planAction === 'Provider recommendation')
      setAddmedicationCard(true)
    else if (planAction === 'Referral')
      onRefClick()
    else if (planAction === 'Medication choices')
      setIsMedChoices(true)
    else if (planAction === 'Medication exclusion') {
      confirmMedRestriction()
    } else if (['Generate care plan', 'Regenerate care plan'].includes(planAction))
      setConfirmOpen(true)
    else if (['Move to baseline', 'Reset care plan'].includes(planAction))
      setConfirmResetOpen(true)

    handleClose()
    return
  }

  return (<>
    {planloading && <CircularProgress className={classes.planLoading} />}
    {Helper.checkGroup(environment.okta.providerGroup, currentRole) && <>
      <IconButton
        aria-label="more"
        id="long-button"
        disabled={(planloading || isDisabled)}
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        className={classes.zeroPadding}
        sx={{ verticalAlign: 'text-top' }}
      >
        <MoreVertOutlinedIcon className={classes.editIcon} />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          '& .Mui-disabled': {
            opacity: '1 !important'
          }
        }}
      >
        {options?.map((option) => (
          <MenuItem
            key={option}
            disabled={(['Generate care plan', 'Regenerate care plan', 'Provider recommendation', 'Reset care plan', 'Referral', 'Medication exclusion'].includes(option) && prgStatus)
              || (option === 'Regenerate care plan' && prgPendingCount > 0)
              || (option === 'Provider recommendation' &&
                !(((multiProgramConfirm.find((m) => m.program === currPrg)?.pending || 0) === totalRecomNeedsUpdate))
              )
            }
            value={option}
            selected={option === currentPlanAction}
            onClick={handlePlanAction}
            className={classes.desc}
            sx={{
              '&.Mui-disabled': {
                color: prgStatus ? 'grey !important' : '',
              },
              '&.MuiMenuItem-root, & .MuiListItemIcon-root': {
                padding: '0.3rem  0.5rem !important',
              },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>
    </>
    }

    {addmedicationCard && (
      <ProviderRecommendationComponent
        open={addmedicationCard}
        careplanId={careplanId}
        currPrg={currPrg}
        patientId={patientId}
        onProvierSubmit={onProviderSubmit}
        handleClose={() => handleDialogClose()}
      />
    )}
    {confirmOpen &&
      <ConfirmationDialogComponent
        open={confirmOpen}
        content={`Are you sure to ${currStage === 'med_baseline' && !isRecomMed && prgPendingCount === 0 ? 'generate' : 'regenerate'} care plan?`}
        handleConfirmClose={handleConfirmClose}
        confirmStatus={confirmStatus} />
    }
    {confirmResetOpen &&
      <ConfirmationDialogComponent
        open={confirmResetOpen}
        content={`Are you sure ${currStage === 'med_baseline' ? 'to reset the care plan?' : 'you want to move to baseline?'}`}
        handleConfirmClose={handleConfirmResetClose}
        confirmStatus={confirmResetStatus} />
    }
    {isMedChoices &&
      <MedChoicesComponent
        open={isMedChoices}
        handleClose={closeMedChoicesDialog}
        id={careplanId}
        title={title}
        category={'Treatments'}
      />
    }
  </>
  )
}

export default withStyles(stylesheet)(LongMenu)
